import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  BarChart3,
  GitCompare,
  TrendingUp,
  Lightbulb,
  ShieldCheck,
  BrainCircuit,
  Workflow,
  Globe,
  Database,
  BadgeCheck,
  Share2,
  FileSearch,
  Users,
  Sparkles,
  Rocket,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { PLATFORM_PAGE, BOOK_DEMO_URL } from "../data/mock";
import { FadeIn, SplitText, BlurText, SpotlightCard, HexPattern, Eyebrow, Magnetic, FloatingHexes } from "../components/reactbits/Anim";

const ICONS = {
  BarChart3,
  GitCompare,
  TrendingUp,
  Lightbulb,
  ShieldCheck,
  BrainCircuit,
  Workflow,
  Globe,
  Database,
  BadgeCheck,
  Share2,
  FileSearch,
  Users,
  Sparkles,
  Rocket,
};

const cardCls =
  "rounded-2xl border border-[#EDE5D3] bg-white shadow-[0_2px_16px_rgba(31,27,19,0.04)] transition-all duration-300 hover:border-[#F0A322]/50 hover:shadow-[0_8px_30px_rgba(232,147,12,0.12)]";

const Platform = () => (
  <div className="bg-white text-[#1F1B13]" data-testid="platform-page">
    {/* ===== Page hero ===== */}
    <section className="relative overflow-hidden bg-[#FDFBF7] pt-40 pb-24">
      <HexPattern opacity={0.07} />
      <FloatingHexes />
      <div
        className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[760px] -translate-x-1/2 rounded-full"
        style={{ background: "radial-gradient(ellipse at center, rgba(240,163,34,0.13), transparent 60%)" }}
      />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <Eyebrow>platform</Eyebrow>
        <h1 className="mt-6 max-w-4xl font-heading text-4xl font-bold uppercase leading-tight sm:text-6xl" data-testid="platform-page-heading">
          <SplitText text="MEDHIVE'S" />{" "}
          <span className="text-[#E8930C]">
            <SplitText text="AI-POWERED" delay={0.2} />
          </span>{" "}
          <SplitText text="SOLUTIONS" delay={0.4} />
        </h1>
        <p className="mt-6 max-w-2xl text-lg font-semibold text-[#37311F]">{PLATFORM_PAGE.sub}</p>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-[#5D564A]">{PLATFORM_PAGE.body}</p>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          {PLATFORM_PAGE.checks.map((c) => (
            <span key={c} className="inline-flex items-center gap-2 rounded-full border border-[#F0A322]/35 bg-white px-4 py-2 text-sm font-semibold text-[#37311F]">
              <CheckCircle2 className="h-4 w-4 text-[#E8930C]" /> {c}
            </span>
          ))}
        </div>
        <div className="mt-10">
          <Magnetic>
            <a
              href={BOOK_DEMO_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="platform-book-demo"
              className="group inline-flex items-center gap-2.5 rounded-full bg-[#F0A322] px-8 py-4 text-sm font-semibold uppercase tracking-wide text-[#231A07] transition-colors duration-200 hover:bg-[#E8930C]"
            >
              book demo
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </a>
          </Magnetic>
        </div>
      </div>
    </section>

    {/* ===== Solutions ===== */}
    {PLATFORM_PAGE.solutions.map((sol, idx) => (
      <section
        key={sol.id}
        id={sol.id}
        className={`relative overflow-hidden py-24 lg:py-28 ${idx % 2 ? "bg-[#FAF6EE]" : "bg-white"}`}
        data-testid={`platform-${sol.id}`}
      >
        {idx % 2 === 1 && <HexPattern opacity={0.04} />}
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <div className={`grid items-center gap-14 lg:grid-cols-2 ${idx % 2 ? "lg:[direction:rtl]" : ""}`}>
            <FadeIn className="lg:[direction:ltr]">
              <Eyebrow>{sol.eyebrow}</Eyebrow>
              <h2 className="mt-6 font-heading text-3xl font-bold uppercase leading-tight text-[#1F1B13] sm:text-4xl lg:text-5xl">
                <BlurText text={sol.title} />
              </h2>
              <p className="mt-6 text-base leading-relaxed text-[#5D564A]">{sol.body}</p>
              <Magnetic strength={0.2}>
                <a
                  href={BOOK_DEMO_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#B45309]"
                >
                  book demo
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </a>
              </Magnetic>
            </FadeIn>
            <div className="space-y-4 lg:[direction:ltr]">
              {sol.points.map((p, i) => {
                const Icon = ICONS[p.icon];
                return (
                  <FadeIn key={p.text} delay={i * 0.08}>
                    <SpotlightCard className={`${cardCls} flex items-center gap-4 p-5`}>
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#F0A322]/[0.12] text-[#B45309]">
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className="text-sm font-medium text-[#37311F]">{p.text}</span>
                    </SpotlightCard>
                  </FadeIn>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    ))}

    {/* ===== FAQ ===== */}
    <section className="relative overflow-hidden bg-[#FDFBF7] py-24 lg:py-32" id="faq">
      <HexPattern opacity={0.04} />
      <div className="relative mx-auto max-w-4xl px-6 lg:px-10">
        <FadeIn className="text-center">
          <Eyebrow>{PLATFORM_PAGE.faqEyebrow}</Eyebrow>
          <h2 className="mt-6 font-heading text-3xl font-bold uppercase text-[#1F1B13] sm:text-5xl" data-testid="faq-heading">
            <BlurText text="FREQUENTLY ASKED" /> <span className="text-[#E8930C]"><BlurText text="QUESTIONS" delay={0.2} /></span>
          </h2>
          <p className="mt-5 text-base text-[#5D564A]">{PLATFORM_PAGE.faqBody}</p>
        </FadeIn>

        <FadeIn delay={0.15}>
          <Accordion type="single" collapsible className="mt-12 space-y-3" data-testid="faq-accordion">
            {PLATFORM_PAGE.faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="rounded-xl border border-[#EDE5D3] bg-white px-6 shadow-[0_2px_10px_rgba(31,27,19,0.03)] data-[state=open]:border-[#F0A322]/50"
              >
                <AccordionTrigger className="text-left font-heading text-base font-semibold text-[#1F1B13] hover:text-[#B45309] hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-[#5D564A]">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </FadeIn>
      </div>
    </section>
  </div>
);

export default Platform;
